import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { LocationDescriptor as To } from "history";
import React from "react";

import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { Count, NotificationsBadge } from "@/mk2/components/NotificationsBadge";
import { HistoryLocationState } from "@/mk2/services/browserHistory";

import styles from "./ListItem.mscss";

export interface OwnProps {
    /** Custom style properties */
    style?: React.CSSProperties;

    /** Add custom classname to container */
    className?: string;

    /** Destination for the react router <Link> */
    to?: To;
    toState?: Partial<HistoryLocationState>;

    /** Custom icon for the item */
    icon?: React.ReactNode;

    /** If true it will display right chevron icon on the right */
    hasMore?: boolean;

    /** Change has more icon */
    hasMoreIcon?: React.ReactNode;

    /** If true bottom border will not be displayed */
    isLast?: boolean;

    /** Optional element displayed before the count number */
    rightElement?: React.ReactNode;

    /** Number displayed as counter */
    count?: Count;

    /** Display count as badge */
    asBadge?: boolean;

    /** Click handler for the component */
    onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>;

    /** Mouse enter handler for the component */
    onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>;

    /** Cypress handler */
    "data-cy"?: string;
}

export class ListItem extends React.PureComponent<OwnProps> {
    public render() {
        const {
            className,
            to,
            toState,
            style,
            icon,
            children,
            hasMore,
            hasMoreIcon,
            isLast,
            count,
            onClick,
            onMouseEnter,
            asBadge,
            rightElement,
            "data-cy": dataCy,
        } = this.props;

        const box = (
            <div className={styles.ListItem__box}>
                {icon && <div className={styles.ListItem__icon}>{icon}</div>}
                <div className={styles.ListItem__content}>{children}</div>
                {rightElement && (
                    <div className={styles.ListItem__rightElement}>{rightElement}</div>
                )}
                {count !== undefined && (
                    <div
                        className={cx(
                            styles.ListItem__count,
                            asBadge && styles["ListItem__count--asBadge"],
                        )}
                    >
                        {asBadge ? <NotificationsBadge count={count} /> : count}
                    </div>
                )}
                {hasMore && (
                    <div className={styles.ListItem__hasMore}>
                        {hasMoreIcon || <FontAwesomeIcon icon={faAngleRight} />}
                    </div>
                )}
            </div>
        );

        if (to) {
            return (
                <Link
                    className={cx(styles.ListItem, isLast && styles["ListItem--last"], className)}
                    style={style}
                    to={
                        typeof to === "string"
                            ? { pathname: to, state: toState }
                            : { ...to, state: { ...to.state, ...toState } }
                    }
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                    data-cy={dataCy}
                >
                    {box}
                </Link>
            );
        } else {
            return (
                <div
                    className={cx(
                        styles.ListItem,
                        isLast && styles["ListItem--last"],
                        onClick && styles["ListItem--clickable"],
                        className,
                    )}
                    style={style}
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                    data-cy={dataCy}
                >
                    {box}
                </div>
            );
        }
    }
}

interface PlaceholderOwnProps {
    /** Custom CSS style properties */
    style?: React.CSSProperties;

    /** If true it will display icon placeholder */
    icon?: boolean | React.ReactNode;

    /** If true bottom border will not be displayed */
    isLast?: boolean;
}

export const ListItemPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({
    style,
    icon,
    isLast,
}) => (
    <div className={cx(styles.ListItem, isLast && styles["ListItem--last"])} style={style}>
        <div className={styles.ListItem__box} style={{ height: icon ? undefined : 44 }}>
            {icon && (
                <div className={styles.ListItem__icon}>
                    {icon === true ? (
                        <LoadingPlaceholder
                            width={32}
                            height={32}
                            style={{ borderRadius: "4px" }}
                        />
                    ) : (
                        icon
                    )}
                </div>
            )}
            <div className={styles.ListItem__content}>
                <LoadingPlaceholder width="100%" height={14} />
            </div>
        </div>
    </div>
);
ListItemPlaceholder.displayName = "ListItemPlaceholder";
